import { ClientContext } from './client-context';

/**
 * @callback Get
 * @param {string} key The key of the value to retrieve.
 * @returns {any | undefined} The value associated with the key, or `undefined` if the key does not exist.
 */
/**
 * @callback Set
 * @param {string} key The key to set.
 * @param {any} value The value to associate with the key.
 */
/**
 * @typedef RequestContext
 * @property {Get} get Retrieves a value from the context based on the provided key.
 * @property {Set} set Sets a key-value pair in the context.
 */

/**
 *
 * Initializes the request context store based on the environment.
 * In a Node.js environment, it's using `AsyncLocalStorage` instance from `server.js`.
 * In a client-side environment, we are initializing context.
 * @returns {AsyncLocalStorage | ClientContext} The initialized context store.
 */
const context = global.AsyncLocalStorage ?? new ClientContext();

function getStore() {
  return context.getStore?.();
}

/**
 * The singleton instance of the request context manager.
 * Provides methods to `get`, `set`, and `run` (in Node.js) values within the request context.
 * @type {RequestContext}
 */
export const requestContext = {
  /**
   * @type Get
   */
  get: key => {
    const store = getStore();

    if (!store) return undefined;

    return store.get(key);
  },

  /**
   * @type Set
   */
  set: (key, value) => {
    const store = getStore();

    if (!store) return;

    store.set(key, value);
  },

  /**
   * wrapper for https://nodejs.org/api/async_context.html#asynclocalstoragerunstore-callback-args
   */
  run: (store, callback) => {
    return context.run(store, callback);
  },
};
